import React, { useEffect, useContext } from "react";
import { RouteComponentProps, Route } from "react-router-dom";
import { Box, Typography } from "@material-ui/core";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CallState,
  DISRUPTION_ITINERARY_ID_QUERY_PARAM,
  FlightItineraryState,
  GetExerciseEligibilitiesRequestV1ByItineraryIds,
  GetExerciseEligibilitiesRequestV1Enum,
} from "redmond";
import { LoadingPopup, B2BSpinner, ButtonWrap, useDeviceTypes } from "halifax";
import clsx from "clsx";
import dayjs from "dayjs";

import { useIsTravelApp } from "@capone/common";
import { DisruptionProtectionConnectorProps } from "./container";
import {
  PORTAL_TITLE,
  getDisruptionProtectionTitle,
  MCP_TITLE,
} from "../../lang/textConstants";
import {
  BASE_PATH_HOME,
  PATH_DISRUPTION_PROTECTION_OVERVIEW,
  PATH_DISRUPTION_PROTECTION_REFUND,
  PATH_DISRUPTION_PROTECTION_REBOOK_FLIGHT,
  PATH_DISRUPTION_PROTECTION_MCP_REBOOK_CONNECTING_FLIGHT,
  PATH_DISRUPTION_PROTECTION_REBOOK_CONNECTION,
  PATH_DISRUPTION_PROTECTION_CONTACT_SUPPORT,
} from "../../utils/paths";
import {
  pushToDisruptionOverview,
  pushToTripsHome,
} from "../../utils/queryStringHelpers";
import {
  DisruptionProtectionBanner,
  DisruptionProtectionLanding,
  DisruptionProtectionContactSupport,
  DisruptionProtectionFooter,
  DisruptionProtectionRebook,
} from "./components";
import { MyTripsHeader } from "../common";
import * as constants from "./constants";
import "./styles.scss";
import { DisruptionProtectionRefund } from "./components/DisruptionProtectionRefund";
import { ClientContext } from "../../App";
import { MissedConnectionProtectionLanding } from "./components/MissedConnectionProtectionLanding";

export interface IDisruptionProtectionProps
  extends DisruptionProtectionConnectorProps,
    RouteComponentProps {}

export const DisruptionProtection = ({
  history,
  areAllFlightFetchingInTerminalState,
  areAllFlightFetchingNotCalled,
  disruptionProtectionItinerary,
  fetchFlights,
  setSelectedFlightDisruptionProtectionItineraryId,
  selectedFlightDisruptionProtectionItineraryId,
  fetchFlightDisruptions,
  areSomeDisruptionInfoFetchingsInProcess,
  setDisruptionFlightRefundResult,
  setSubmitDisruptionFlightRefundCallState,
  isDisruptionProtection24hRuleSatisfied,
  isPostBookingOfferMyTripsEnabled,
  flightDisruptions,
}: IDisruptionProtectionProps) => {
  const { matchesMobile } = useDeviceTypes();
  const { isAgentPortal } = useContext(ClientContext);
  const fdaProductVariant =
    disruptionProtectionItinerary?.ancillaries.delay?.productVariant
      ?.ProductVariant;
  const fdaHeaderTitle = constants.getHeaderTitle(fdaProductVariant);
  const [openContactModal, setOpenContactModal] =
    React.useState<boolean>(false);
  const isMissedConnectionProtecion =
    history.location.pathname ===
    PATH_DISRUPTION_PROTECTION_MCP_REBOOK_CONNECTING_FLIGHT;

  // Virtual Interline Flights will offer only one type of fare the most basic is safe to only check the first index
  const isVITripSelected =
    disruptionProtectionItinerary?.bookedItinerary.travelItinerary.slices.some(
      (slice) => slice.segments.some((segment) => segment.isSelfTransferLayover)
    );

  useEffect(() => {
    window.scrollTo(0, 0);

    setSelectedFlightDisruptionProtectionItineraryId(null);

    // note: it sets the selected disruption-protection itinerary id
    const urlSearchParams = new URLSearchParams(history.location.search);
    const disruptionProtectionItineraryIdFromQuery = urlSearchParams.get(
      DISRUPTION_ITINERARY_ID_QUERY_PARAM
    );
    if (disruptionProtectionItineraryIdFromQuery) {
      setSelectedFlightDisruptionProtectionItineraryId(
        disruptionProtectionItineraryIdFromQuery
      );
    }

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  useEffect(() => {
    document.title = isMissedConnectionProtecion
      ? MCP_TITLE
      : getDisruptionProtectionTitle(fdaProductVariant);
  }, [isMissedConnectionProtecion, fdaProductVariant]);

  const { isTravelApp } = useIsTravelApp();

  useEffect(() => {
    if (selectedFlightDisruptionProtectionItineraryId) {
      setDisruptionFlightRefundResult(null);
      setSubmitDisruptionFlightRefundCallState(CallState.NotCalled);
    }
  }, [selectedFlightDisruptionProtectionItineraryId]);

  const disruptionContractId =
    disruptionProtectionItinerary?.ancillaries.delay?.id.quoteId;

  const currentEligibility =
    flightDisruptions?.eligibilityByItinerary[
      selectedFlightDisruptionProtectionItineraryId ?? ""
    ];
  const disruptionExerciseURL = currentEligibility?.disruptionExerciseURL;

  useEffect(() => {
    // note: wait for disruptionProtectionItinerary to resolve so contractId reflects
    // its final value (defined, or legitimately absent) instead of firing early with
    // contractId undefined and then again once the itinerary loads.
    if (selectedFlightDisruptionProtectionItineraryId && disruptionProtectionItinerary) {
      fetchFlightDisruptions({
        itineraryIds: [selectedFlightDisruptionProtectionItineraryId],
        contractId: disruptionContractId,
        GetExerciseEligibilitiesRequestV1:
          GetExerciseEligibilitiesRequestV1Enum.ByItineraryIds,
      } as GetExerciseEligibilitiesRequestV1ByItineraryIds);
    }
  }, [
    selectedFlightDisruptionProtectionItineraryId,
    disruptionProtectionItinerary,
    disruptionContractId,
  ]);


  useEffect(() => {
    if (areAllFlightFetchingNotCalled) {
      /*
        TODO: it fetches all flight itineraries - it should be replaced with an endpoint that
        returns a single itinerary (specified by the itinerary id) only.
      */
      fetchFlights(
        {
          states: [FlightItineraryState.Future, FlightItineraryState.Present],
          referenceDateTime: dayjs().format(),
        },
        history,
        isPostBookingOfferMyTripsEnabled
      );
      fetchFlights(
        {
          states: [FlightItineraryState.Past, FlightItineraryState.Canceled],
          referenceDateTime: dayjs().format(),
        },
        history
      );
    }
  }, [areAllFlightFetchingNotCalled]);

  useEffect(() => {
    if (
      areAllFlightFetchingInTerminalState &&
      !selectedFlightDisruptionProtectionItineraryId &&
      (!disruptionProtectionItinerary ||
        (!disruptionProtectionItinerary.ancillaries.delay &&
          !disruptionProtectionItinerary.ancillaries.missedConnection))
    ) {
      pushToTripsHome({ history });
    }
  }, [
    selectedFlightDisruptionProtectionItineraryId,
    disruptionProtectionItinerary,
    areAllFlightFetchingInTerminalState,
  ]);

  // note: gating logic added for the 24-hour rule; it's not applied to agents
  useEffect(() => {
    if (
      !isAgentPortal &&
      !isDisruptionProtection24hRuleSatisfied(
        disruptionProtectionItinerary?.departureTime
      )
    ) {
      pushToTripsHome({ history });
    }
  }, [isDisruptionProtection24hRuleSatisfied, disruptionProtectionItinerary]);

  const disruptionNavigatorCopy: {
    title: string;
    subtitle?: string;
  } = (() => {
    switch (history.location.pathname) {
      case PATH_DISRUPTION_PROTECTION_OVERVIEW:
      case PATH_DISRUPTION_PROTECTION_CONTACT_SUPPORT:
        if (matchesMobile) {
          return {
            title: fdaHeaderTitle,
            subtitle: constants.GET_HELP_WITH_TRIP,
          };
        } else {
          return {
            title: constants.BACK_TO_MY_TRIPS,
          };
        }
      case PATH_DISRUPTION_PROTECTION_REFUND:
      case PATH_DISRUPTION_PROTECTION_REBOOK_FLIGHT:
      case PATH_DISRUPTION_PROTECTION_REBOOK_CONNECTION:
        if (matchesMobile) {
          return {
            title: fdaHeaderTitle,
            subtitle: constants.GET_HELP_WITH_TRIP,
          };
        } else {
          return {
            title: constants.GO_BACK,
          };
        }
      case PATH_DISRUPTION_PROTECTION_MCP_REBOOK_CONNECTING_FLIGHT: {
        if (matchesMobile) {
          return {
            title: isVITripSelected
              ? constants.MCR_HEADER_TITLE
              : constants.MCP_HEADER_TITLE,
            subtitle: constants.GET_HELP_WITH_TRIP,
          };
        } else {
          return {
            title: constants.BACK_TO_MY_TRIPS,
          };
        }
      }
      default:
        return {
          title: constants.BACK_TO_MY_TRIPS,
        };
    }
  })();

  const handleGoBack = () => {
    switch (history.location.pathname) {
      case PATH_DISRUPTION_PROTECTION_OVERVIEW:
      case PATH_DISRUPTION_PROTECTION_CONTACT_SUPPORT:
      case PATH_DISRUPTION_PROTECTION_MCP_REBOOK_CONNECTING_FLIGHT:
        pushToTripsHome({
          history,
          itineraryId: disruptionProtectionItinerary?.bookedItinerary.id,
        });
        break;
      case PATH_DISRUPTION_PROTECTION_REFUND:
      case PATH_DISRUPTION_PROTECTION_REBOOK_FLIGHT:
      case PATH_DISRUPTION_PROTECTION_REBOOK_CONNECTION:
        pushToDisruptionOverview({
          history,
          itineraryId: disruptionProtectionItinerary?.bookedItinerary.id,
        });
        break;
      default:
        break;
    }
  };

  const DisruptionProtectionBody = (
    <Box className="disruption-protection-body">
      {matchesMobile && !disruptionExerciseURL && (
        <MobileNavigator
          {...disruptionNavigatorCopy}
          onClick={isTravelApp ? undefined : handleGoBack}
        />
      )}
      {/* note: disruption protection banner is displayed on the landing page only */}
      <Route
        path={
          disruptionExerciseURL
            ? [
                PATH_DISRUPTION_PROTECTION_CONTACT_SUPPORT,
                PATH_DISRUPTION_PROTECTION_MCP_REBOOK_CONNECTING_FLIGHT,
              ]
            : [
                PATH_DISRUPTION_PROTECTION_OVERVIEW,
                PATH_DISRUPTION_PROTECTION_CONTACT_SUPPORT,
                PATH_DISRUPTION_PROTECTION_MCP_REBOOK_CONNECTING_FLIGHT,
              ]
        }
        exact
      >
        <DisruptionProtectionBanner
          isMissedConnectionProtecion={isMissedConnectionProtecion}
          fdaDisplayName={fdaHeaderTitle}
        />
      </Route>
      {!matchesMobile && !disruptionExerciseURL && (
        <Box className="go-back-button-wrapper">
          <GoBackButton {...disruptionNavigatorCopy} onClick={handleGoBack} />
        </Box>
      )}
      <Route path={[PATH_DISRUPTION_PROTECTION_OVERVIEW]} exact>
        <DisruptionProtectionLanding />
      </Route>
      <Route path={[PATH_DISRUPTION_PROTECTION_CONTACT_SUPPORT]} exact>
        <DisruptionProtectionContactSupport />
      </Route>
      <Route path={[PATH_DISRUPTION_PROTECTION_REFUND]} exact>
        <DisruptionProtectionRefund />
      </Route>
      <Route
        path={PATH_DISRUPTION_PROTECTION_MCP_REBOOK_CONNECTING_FLIGHT}
        exact
      >
        <MissedConnectionProtectionLanding isAgentPortal={isAgentPortal} />
      </Route>
      <Route
        path={[
          PATH_DISRUPTION_PROTECTION_REBOOK_FLIGHT,
          PATH_DISRUPTION_PROTECTION_REBOOK_CONNECTION,
        ]}
        exact
      >
        <DisruptionProtectionRebook isMobile={matchesMobile} />
      </Route>
    </Box>
  );

  return (
    <Box
      className={clsx("disruption-protection-root", { mobile: matchesMobile })}
    >
      <Box className="disruption-protection-container">
        <LoadingPopup
          open={areSomeDisruptionInfoFetchingsInProcess}
          indicator={B2BSpinner}
          message={constants.LOADING_FLIGHT_DETAILS}
          classes={(() => {
            const classes = ["disruption-protection-loading-modal"];
            if (matchesMobile) classes.push("mobile");
            return classes;
          })()}
        />
        <MyTripsHeader
          className="disruption-protection-trips-header"
          onClick={() => history.push(BASE_PATH_HOME)}
          tripHeaderCopy={{
            title: isMissedConnectionProtecion
              ? isVITripSelected
                ? constants.MCR_HEADER_TITLE
                : constants.MCP_HEADER_TITLE
              : fdaHeaderTitle,
            subtitle: isMissedConnectionProtecion
              ? constants.MCP_HEADER_SUBTITLE
              : constants.HEADER_SUBTITLE,
          }}
          hidden={matchesMobile}
          contactModalController={{
            openContactModal,
            setOpenContactModal,
          }}
        />
        {DisruptionProtectionBody}
        {/* note: disruption protection footer is displayed on the landing page only */}
        <Route
          path={
            disruptionExerciseURL
              ? [PATH_DISRUPTION_PROTECTION_MCP_REBOOK_CONNECTING_FLIGHT]
              : [
                  PATH_DISRUPTION_PROTECTION_OVERVIEW,
                  PATH_DISRUPTION_PROTECTION_MCP_REBOOK_CONNECTING_FLIGHT,
                ]
          }
          exact
        >
          <DisruptionProtectionFooter
            isMissedConnectionProtecion={isMissedConnectionProtecion}
          />
        </Route>
      </Box>
    </Box>
  );
};

interface IMobileNavigatorProps {
  title: string;
  subtitle?: string;
  onClick?: () => void;
}

export const MobileNavigator = ({
  title,
  subtitle,
  onClick,
}: IMobileNavigatorProps) => {
  return (
    <Box className="mobile-navigator-root">
      {onClick && (
        <FontAwesomeIcon
          className="go-back-button"
          icon={faChevronLeft}
          onClick={onClick}
        />
      )}
      <Box className="info-section">
        <Typography className="title-copy" variant="subtitle1">
          {title}
        </Typography>
        {subtitle && (
          <Typography className="subtitle-copy" variant="caption">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface IGoBackButtonProps {
  title: string;
  onClick: () => void;
}

export const GoBackButton = ({ title, onClick }: IGoBackButtonProps) => {
  return (
    <ButtonWrap className="go-back-button-root" onClick={onClick}>
      <FontAwesomeIcon className="go-back-icon" icon={faChevronLeft} />
      <Typography className="button-copy" variant="subtitle1">
        {title}
      </Typography>
    </ButtonWrap>
  );
};
